<template>
<div class="ChecklistStatusDialogCategory">
  <div class="text--primary font-weight-medium py-2">
    {{ $t(`common.statusCategoryNames.${category.name}`) }}
  </div>

  <div class="ChecklistStatusDialogCategory__statusContainer">
    <Draggable
      item-key="id"
      :list="category.statuses"
      handle=".ChecklistStatusDialogCategory__grabIcon"
      class="ChecklistStatusDialogCategory__draggableArea"
      group="statuses"
      :forceAutoScrollFallback="true"
      :emptyInsertThreshold="100"
      :delay="500"
      :delayOnTouchOnly="true"
    >
      <template #item="{ element: status }">
        <div
             :key="`${category.name}-status-${status.label}`"
             class="ChecklistStatusDialogCategory__statusFlexContainer"
        >
            <div v-if="editMode && status.isEditable" class="ChecklistStatusDialogCategory__grabIcon pr-2">
              <font-awesome-icon :icon="['fas', 'grip-vertical']" />
            </div>

            <div class="status-chip-tick-wrapper d-flex align-center justify-space-between w-100 column-gap-2 py-2 px-1 rounded-lg"
                 :class="{ 'selectable': !editMode && category.areStatusesSelectable }"
                 @click="category.areStatusesSelectable && onStatusClick(status.id)"
            >
              <AppTooltip :disabled="status.label.length <= statusLabelMaxDisplayedLength">
                <template #activator="{ props }">
                  <v-chip
                    class="ChecklistStatusDialogCategory__status font-weight-medium text-white px-2"
                    :color="status.color || statusCategoryColor"
                    :disabled="!category.areStatusesSelectable"
                    variant="flat"
                    v-bind="props"
                  >
                    {{ status.label }}
                  </v-chip>
                </template>
                <span>{{ status.label }}</span>
              </AppTooltip>
              <div v-if="!editMode">
                <app-icon-ds
                  icon="check"
                  size="sm"
                  color="darkblue-500"
                  :style="{ visibility: (status.id === selectedStatusId) ? 'visible' : 'hidden' }"
                />
              </div>
            </div>

            <div v-if="editMode && status.isEditable" class="ChecklistStatusDialogCategory__edition-buttons ml-2">
              <AppTooltip>
                <template #activator="{ props }">
                  <AppButton icon
                             :width="32"
                             :height="32"
                             v-bind="props"
                             @click="() => onStatusDelete(status)"
                  >
                    <font-awesome-icon class="text-greyDark-lighten-1" :icon="['fas', 'trash']"></font-awesome-icon>
                  </AppButton>
                </template>
                <span>{{ $t('project.checklist.dialogs.ChecklistStatusDialogCategory.deleteStatusTooltip') }}</span>
              </AppTooltip>

              <ChecklistStatusDialogEditMenu :status="status"
                                            :category="category.name"
                                            @edit="onStatusEdit"
              />
            </div>
        </div>
      </template>
    </Draggable>
  </div>

  <div v-if="!editMode && currentUserCanCreateStatus" class="mt-5 mr-3">
    <ChecklistStatusDialogNewStatus :category="category.name" />
  </div>

  <ChecklistStatusDialogDeleteConfirmation v-if="statusToDelete"
                                    :status="statusToDelete"
                                    @confirm="onConfirmStatusDeletion"
                                    @close="onCancelStatusDeletion"
  />
</div>
</template>

<script>
import Draggable from 'vuedraggable'
import { mapGetters } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import ChecklistStatusDialogDeleteConfirmation from '@/project/checklist/dialogs/ChecklistStatusDialogDeleteConfirmation.vue'
import ChecklistStatusDialogEditMenu from '@/project/checklist/dialogs/ChecklistStatusDialogEditMenu.vue'
import ChecklistStatusDialogNewStatus from '@/project/checklist/dialogs/ChecklistStatusDialogNewStatus.vue'
import { statusColors } from '@/project/checklist/dialogs/taskStatus'

const STATUS_LABEL_MAX_DISPLAYED_LENGTH_IN_EDITION = 18
const STATUS_LABEL_MAX_DISPLAYED_LENGTH_IN_READONLY = 33

export default {
  name: 'ChecklistStatusDialogCategory',
  components: {
    Draggable,
    AppButton,
    AppTooltip,
    ChecklistStatusDialogDeleteConfirmation,
    ChecklistStatusDialogEditMenu,
    ChecklistStatusDialogNewStatus,
  },
  props: {
    category: {
      type: Object,
      required: true,
    },
    isBulkEdition: {
      type: Boolean,
    },
    selectedStatusId: {
      type: Number,
      required: true,
    },
    editMode: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      statusToDelete: null,
    }
  },
  computed: {
    ...mapGetters('room', ['isCurrentUserPm']),
    statusCategoryColor () {
      return statusColors[this.category.name]
    },
    currentUserCanCreateStatus () {
      return this.isCurrentUserPm && !this.isBulkEdition
    },
    statusLabelMaxDisplayedLength () {
      return this.editMode
        ? STATUS_LABEL_MAX_DISPLAYED_LENGTH_IN_EDITION
        : STATUS_LABEL_MAX_DISPLAYED_LENGTH_IN_READONLY
    },
  },
  methods: {
    onStatusClick (statusId) {
      this.$emit('update:selectedStatusId', statusId)
    },
    onStatusEdit (editedStatus) {
      this.$emit('status-edit', { editedCategory: this.category.name, editedStatus })
    },
    onStatusDelete (statusToDelete) {
      this.statusToDelete = statusToDelete
    },
    onConfirmStatusDeletion () {
      this.$emit('status-delete', { status: this.statusToDelete, category: this.category.name })
      this.statusToDelete = null
    },
    onCancelStatusDeletion () {
      this.statusToDelete = null
    },
  },
}
</script>

<style scoped lang="scss">
.ChecklistStatusDialogCategory {
  display: flex;
  flex-direction: column;
  width: 280px;
  padding: 12px;
  padding-right: 0;
  border: 1px solid #eee;
  border-radius: 6px;

  &__statusContainer {
    display: flex;
    overflow-x: hidden;
    flex: 1;
  }
  &__statusFlexContainer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: nowrap;

    &:last-child {
      margin-bottom: 80px;
    }
  }
  &__draggableArea {
    overflow-y: auto;
    width: 100%;
    padding-right: 12px;
  }
  &__grabIcon {
    font-size: 16px;
    color: rgb(var(--v-theme-greyDark-lighten-1));
    cursor: grab;
  }
  &__status {
    height: auto;
    min-height: 24px;
    text-align: center;
    border: 2px solid transparent;

    &--active {
      border-width: 4px;
      border-color: rgb(var(--v-theme-tertiary)) !important;
    }

    :deep(.v-chip__content) {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      display: inline-block;
    }
  }
  &__edition-buttons {
    flex: 1;
    display: flex;
    justify-content: flex-end;
  }
}
.status-chip-tick-wrapper {
  min-width: 0;
  &.selectable:hover {
    cursor: pointer;
    background-color: rgba(var(--v-theme-grey-900), 0.05);
  }
}
</style>
